import React, { useEffect, useState, useMemo } from 'react'
import { useDebounceValue } from '@sketch/utils'
import {
  Flex,
  Box,
  Text,
  LoadingPlaceholder,
  Checkbox,
  handleFetchMore,
  InfiniteList,
  Input,
  Tooltip,
} from '@sketch/components'
import { FetchMoreFunc } from '@sketch/components/src/InfiniteList/InfiniteList'
import {
  ListContainer,
  PageIcon,
  AllPrototypesIcon,
  PrototypeIcon,
  SearchContainer,
  SearchActions,
  SearchButton,
  Header,
  ErrorIcon,
  TryAgainButton,
} from './ContentsTab.styles'
import { dataIdFromObject } from '@sketch/graphql-cache'
import {
  useGetPagesQuery,
  useGetFramesForDocumentQuery,
} from '@sketch/gql-types'
import { usePreviewForm } from './usePreviewForm'
import { capitalize } from 'lodash'

interface ContentsTabProps {
  shareIdentifier: string
  versionShortId: string
}

const ENTRIES_PATH = ['share', 'version', 'document', 'pages', 'entries']

type ListItem = { name: string; uuid: string }
type SelectedItem = { uuid: string; type: 'PAGE' | 'PROTOTYPE' }

// Helper function to get the page UUID for a prototype
const getPrototypePageUuid = (
  prototypeId: string,
  fullPrototypes: any[]
): string | undefined => {
  return fullPrototypes.find(p => p.uuid === prototypeId)?.page?.uuid
}

interface ItemListProps {
  handleSelectAll: () => void
  toggleUUID: (id: string, type: 'PAGE' | 'PROTOTYPE') => void
  items: ListItem[]
  after: string | null
  fetchMore: FetchMoreFunc
  selectedUUIDs: SelectedItem[]
  allChecked: boolean
  type: 'prototypes' | 'pages'
  requiredPages: string[]
  hasSearch: boolean
}

const ItemList = ({
  handleSelectAll,
  items,
  after,
  fetchMore,
  toggleUUID,
  allChecked,
  selectedUUIDs,
  type,
  requiredPages,
  hasSearch,
}: ItemListProps) => {
  if (hasSearch && items.length === 0) {
    return <>No {type} found.</>
  }

  return (
    <>
      <Box mb={2}>
        <Checkbox
          name={`all${type}`}
          checked={allChecked}
          onChange={handleSelectAll}
        >
          <Flex alignItems="center">
            {type === 'pages' ? <PageIcon /> : <AllPrototypesIcon />}
            All {capitalize(type)}
          </Flex>
        </Checkbox>
      </Box>
      <ListContainer>
        <InfiniteList
          wrappingComponent={React.Fragment}
          renderLoading={() => <LoadingPlaceholder size="64px" />}
          canLoadMore={after !== null}
          onLoadMore={handleFetchMore(fetchMore, ENTRIES_PATH, {
            dataIdFromObject,
            after: after,
          })}
        >
          {items.map(({ uuid, name }) => {
            if (uuid === null) {
              return null
            }

            const isMandatoryPage =
              type === 'pages' && requiredPages.includes(uuid)

            if (isMandatoryPage) {
              return (
                <Box mb={2} key={uuid}>
                  <Tooltip content="This page is included with your prototype selection">
                    <Checkbox
                      name="selectedUUIDs"
                      checked={true}
                      disabled
                      onChange={() => toggleUUID(uuid, 'PAGE')}
                    >
                      <Flex alignItems="center">
                        <PageIcon />
                        {name}
                      </Flex>
                    </Checkbox>
                  </Tooltip>
                </Box>
              )
            }

            return (
              <Box mb={2} key={uuid}>
                <Checkbox
                  name="selectedUUIDs"
                  checked={selectedUUIDs.some(item => item.uuid === uuid)}
                  onChange={() =>
                    toggleUUID(uuid, type === 'pages' ? 'PAGE' : 'PROTOTYPE')
                  }
                >
                  <Flex alignItems="center">
                    {type === 'pages' ? <PageIcon /> : <PrototypeIcon />}
                    {name}
                  </Flex>
                </Checkbox>
              </Box>
            )
          })}
        </InfiniteList>
      </ListContainer>
    </>
  )
}

export const ContentsTab = ({
  shareIdentifier,
  versionShortId,
}: ContentsTabProps) => {
  const { values, setFieldValue } = usePreviewForm()
  const selectedUUIDs: SelectedItem[] = useMemo(
    () => values.selectedUUIDs || [],
    [values.selectedUUIDs]
  )
  const [searchTerm, setSearchTerm] = useState('')
  const debouncedSearchTerm = useDebounceValue(searchTerm, 300)
  const [requiredPages, setRequiredPages] = useState<string[]>([])

  const { data, loading, error, fetchMore, refetch } = useGetPagesQuery({
    variables: {
      shareIdentifier,
      versionShortId,
      search: debouncedSearchTerm,
    },
  })

  const prototypeFramesQuery = useGetFramesForDocumentQuery({
    variables: {
      identifier: shareIdentifier,
      versionShortId,
      search: debouncedSearchTerm,
      filter: 'PROTOTYPE',
    },
  })

  const afterPages = data?.share?.version?.document?.pages?.meta.after || null
  const afterPrototypes =
    prototypeFramesQuery.data?.share?.version?.document?.frames.meta.after ||
    null

  const formatEntry = (entries: any[]) =>
    entries
      .map(entry => ({
        name: entry.name,
        uuid: entry.uuid,
      }))
      .filter(
        (page): page is ListItem => page.name !== null && page.uuid !== null
      ) ?? []

  const pages = useMemo(
    () => formatEntry(data?.share?.version?.document?.pages?.entries || []),
    [data?.share?.version?.document?.pages?.entries]
  )

  const prototypes = useMemo(
    () =>
      formatEntry(
        prototypeFramesQuery.data?.share?.version?.document?.frames.entries ||
          []
      ),
    [prototypeFramesQuery.data?.share?.version?.document?.frames.entries]
  )

  const allPageIds = useMemo(() => pages.map(entry => entry.uuid), [pages])

  const allPrototypeIds = useMemo(
    () => prototypes.map(entry => entry.uuid),
    [prototypes]
  )

  useEffect(() => {
    const selectedPrototypeIds = selectedUUIDs
      .filter(item => item.type === 'PROTOTYPE')
      .map(item => item.uuid)

    if (selectedPrototypeIds.length === 0) {
      setRequiredPages([])
      return
    }

    const fullPrototypes =
      prototypeFramesQuery.data?.share?.version?.document?.frames.entries || []

    const required = [
      ...new Set(
        selectedPrototypeIds
          .map(id => getPrototypePageUuid(id, fullPrototypes))
          .filter(Boolean)
      ),
    ] as string[]

    setRequiredPages(required)
  }, [selectedUUIDs, prototypeFramesQuery.data])

  const handleAddUUID = (id: string, type: 'PAGE' | 'PROTOTYPE') => {
    if (!selectedUUIDs.some(item => item.uuid === id)) {
      setFieldValue('selectedUUIDs', [...selectedUUIDs, { uuid: id, type }])
    }
  }

  const handleRemoveUUID = (id: string) => {
    setFieldValue(
      'selectedUUIDs',
      selectedUUIDs.filter(item => item.uuid !== id)
    )
  }

  const toggleUUID = (id: string, type: 'PAGE' | 'PROTOTYPE') => {
    const currentSelected = values.selectedUUIDs || []

    if (currentSelected.some((item: SelectedItem) => item.uuid === id)) {
      handleRemoveUUID(id)
    } else {
      // if we're selecting a prototype, we need to include the page it's set in
      if (type === 'PROTOTYPE') {
        const fullPrototypes =
          prototypeFramesQuery.data?.share?.version?.document?.frames.entries ||
          []
        const pageToSelectUUID = getPrototypePageUuid(id, fullPrototypes)

        // Build array of items to add - start with the prototype
        const itemsToAdd: SelectedItem[] = [{ uuid: id, type: 'PROTOTYPE' }]

        // Add the required page if it exists and isn't already selected
        if (
          pageToSelectUUID &&
          !currentSelected.some(
            (item: SelectedItem) => item.uuid === pageToSelectUUID
          )
        ) {
          itemsToAdd.push({ uuid: pageToSelectUUID, type: 'PAGE' })
        }

        // Add all items in one state update
        setFieldValue('selectedUUIDs', [...currentSelected, ...itemsToAdd])
        return
      }

      // if it's not a prototype, just plain add it
      handleAddUUID(id, type)
    }
  }

  const handleSelectAllPages = () => {
    const areAllFilteredPagesSelected = allPageIds.every(id =>
      selectedUUIDs.some(item => item.uuid === id)
    )
    if (areAllFilteredPagesSelected) {
      // Deselect filtered pages
      setFieldValue(
        'selectedUUIDs',
        selectedUUIDs.filter(item => !allPageIds.includes(item.uuid))
      )
    } else {
      // Select all filtered pages (keep existing prototypes and other pages)
      const newPageItems = allPageIds
        .filter(id => !selectedUUIDs.some(item => item.uuid === id))
        .map(id => ({ uuid: id, type: 'PAGE' as const }))
      setFieldValue('selectedUUIDs', [...selectedUUIDs, ...newPageItems])
    }
  }

  const handleSelectAllPrototypes = () => {
    const areAllPrototypesSelected = allPrototypeIds.every(id =>
      selectedUUIDs.some(item => item.uuid === id)
    )

    const fullPrototypes =
      prototypeFramesQuery.data?.share?.version?.document?.frames.entries || []

    if (areAllPrototypesSelected) {
      // Deselect all prototypes and their associated pages
      const prototypePagesUUIDs = allPrototypeIds
        .map(id => getPrototypePageUuid(id, fullPrototypes))
        .filter(Boolean)

      // Remove prototypes and their associated pages
      setFieldValue(
        'selectedUUIDs',
        selectedUUIDs.filter(
          item =>
            !allPrototypeIds.includes(item.uuid) &&
            !prototypePagesUUIDs.includes(item.uuid)
        )
      )
    } else {
      // Select all prototypes and their required pages
      const currentSelected = values.selectedUUIDs || []
      const unselectedPrototypeIds = allPrototypeIds.filter(
        id => !currentSelected.some((item: SelectedItem) => item.uuid === id)
      )

      const itemsToAdd: SelectedItem[] = []
      const pagesToAdd = new Set<string>()

      // Add all unselected prototypes and their required pages
      unselectedPrototypeIds.forEach(prototypeId => {
        itemsToAdd.push({ uuid: prototypeId, type: 'PROTOTYPE' })

        const pageToSelectUUID = getPrototypePageUuid(
          prototypeId,
          fullPrototypes
        )

        if (
          pageToSelectUUID &&
          !currentSelected.some(
            (item: SelectedItem) => item.uuid === pageToSelectUUID
          ) &&
          !pagesToAdd.has(pageToSelectUUID)
        ) {
          pagesToAdd.add(pageToSelectUUID)
          itemsToAdd.push({ uuid: pageToSelectUUID, type: 'PAGE' })
        }
      })

      setFieldValue('selectedUUIDs', [...currentSelected, ...itemsToAdd])
    }
  }

  const handleGlobalSelectAll = () => {
    const allItems = [
      ...allPageIds.map(id => ({ uuid: id, type: 'PAGE' as const })),
      ...allPrototypeIds.map(id => ({
        uuid: id,
        type: 'PROTOTYPE' as const,
      })),
    ]
    setFieldValue('selectedUUIDs', allItems)
  }

  const handleGlobalDeselectAll = () => {
    setFieldValue('selectedUUIDs', [])
  }

  const isLoading = !!(loading || prototypeFramesQuery.loading)

  if (error || prototypeFramesQuery.error) {
    return (
      <>
        <Text mb={3}>
          Select the prototypes or pages that will be available in this preview.
        </Text>
        <Flex
          justifyContent="center"
          alignItems="center"
          flexDirection="column"
          height={500}
        >
          <ErrorIcon />
          <Text pt={4} pb={4} textAlign="center" fontSize="F">
            Failed to load preview settings
          </Text>
          <Flex>
            <TryAgainButton variant="primary" onClick={() => refetch()}>
              Try Again
            </TryAgainButton>
          </Flex>
        </Flex>
      </>
    )
  }

  return (
    <>
      <Header>
        Select the prototypes or pages that will be available in this preview.
      </Header>

      <SearchContainer>
        <Input
          type="text"
          placeholder="Search…"
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
        />
        <SearchActions>
          <SearchButton
            variant="primary"
            disabled={
              isLoading ||
              selectedUUIDs.length ===
                allPageIds.length + allPrototypeIds.length
            }
            onClick={handleGlobalSelectAll}
          >
            Select All
          </SearchButton>
          <SearchButton
            loading={isLoading}
            disabled={isLoading || selectedUUIDs.length === 0}
            variant="primary"
            onClick={handleGlobalDeselectAll}
          >
            Deselect All
          </SearchButton>
        </SearchActions>
      </SearchContainer>

      {isLoading ? (
        <Flex justifyContent="center" height={384}>
          <LoadingPlaceholder />
        </Flex>
      ) : (
        <Box height={340}>
          <Box mt={5}>
            <ItemList
              hasSearch={searchTerm !== ''}
              type="prototypes"
              handleSelectAll={handleSelectAllPrototypes}
              toggleUUID={toggleUUID}
              fetchMore={fetchMore}
              items={prototypes}
              selectedUUIDs={selectedUUIDs}
              after={afterPrototypes}
              allChecked={allPrototypeIds.every(id =>
                selectedUUIDs.some(item => item.uuid === id)
              )}
              requiredPages={requiredPages}
            />
          </Box>
          <Box mt={5}>
            <ItemList
              hasSearch={searchTerm !== ''}
              type="pages"
              handleSelectAll={handleSelectAllPages}
              toggleUUID={toggleUUID}
              fetchMore={fetchMore}
              items={pages}
              selectedUUIDs={selectedUUIDs}
              after={afterPages}
              allChecked={allPageIds.every(id =>
                selectedUUIDs.some(item => item.uuid === id)
              )}
              requiredPages={requiredPages}
            />
          </Box>
        </Box>
      )}
    </>
  )
}
